import { useState, useMemo, useCallback } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { CaretRight, CaretDown, Image, ToggleLeft, Hash, TextT, BracketsCurly, BracketsSquare } from '@phosphor-icons/react';
import { FilterField, FilterFieldListProps, FieldType } from './types';
import { getAllPaths, getChildPaths, getSelectedCount } from './outputFilterUtils';

const INDENT_PX = 24;

const DARK_COLORS = {
  background: '#1D2939',
  backgroundHover: '#344054',
  text: '#D0D5DD',
  textSecondary: '#667085',
  border: '#344054',
  accent: '#2ED3B7',
  noData: '#475467',
};

const LIGHT_COLORS = {
  background: '#FFFFFF',
  backgroundHover: '#F9FAFB',
  text: '#101828',
  textSecondary: '#667085',
  border: '#EAECF0',
  accent: '#0D9488',
  noData: '#98A2B3',
};

function getTypeIcon(type: FieldType, color: string) {
  const size = 14;
  switch (type) {
    case 'object':
      return <BracketsCurly size={size} color={color} weight="bold" />;
    case 'array':
      return <BracketsSquare size={size} color={color} weight="bold" />;
    case 'string':
      return <TextT size={size} color={color} weight="bold" />;
    case 'number':
      return <Hash size={size} color={color} weight="bold" />;
    case 'image':
      return <Image size={size} color={color} weight="bold" />;
    case 'boolean':
      return <ToggleLeft size={size} color={color} weight="bold" />;
    case 'null':
      return (
        <Typography sx={{ fontSize: '10px', fontWeight: 700, color, lineHeight: 1, fontFamily: 'monospace' }}>
          null
        </Typography>
      );
    default:
      return <TextT size={size} color={color} weight="bold" />;
  }
}

function getFieldCheckState(
  field: FilterField,
  selectedSet: Set<string>,
): 'checked' | 'unchecked' | 'indeterminate' {
  if (!field.children || field.children.length === 0) {
    return selectedSet.has(field.path) ? 'checked' : 'unchecked';
  }
  const childLeafPaths = getChildPaths(field);
  const selectedChildren = childLeafPaths.filter((p) => selectedSet.has(p)).length;
  if (selectedChildren === 0) return 'unchecked';
  if (selectedChildren === childLeafPaths.length) return 'checked';
  return 'indeterminate';
}

interface FieldRowProps {
  field: FilterField;
  depth: number;
  selectedSet: Set<string>;
  expandedPaths: Set<string>;
  onTogglePath: (path: string) => void;
  onToggleExpand: (path: string) => void;
  isDark: boolean;
}

function FieldRow({ field, depth, selectedSet, expandedPaths, onTogglePath, onToggleExpand, isDark }: FieldRowProps) {
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;
  const hasChildren = field.children && field.children.length > 0;
  const isExpanded = expandedPaths.has(field.path);
  const checkState = getFieldCheckState(field, selectedSet);
  const isNoData = field.type === 'null' || field.preview === '[]';
  const handleCheckboxChange = useCallback(() => {
    onTogglePath(field.path);
  }, [field.path, onTogglePath]);
  const handleExpandClick = useCallback(() => {
    onToggleExpand(field.path);
  }, [field.path, onToggleExpand]);
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          paddingLeft: `${depth * INDENT_PX}px`,
          paddingY: '2px',
          cursor: 'pointer',
          '&:hover': { backgroundColor: colors.backgroundHover },
          borderRadius: '4px',
          minHeight: '32px',
        }}
        onClick={handleCheckboxChange}
      >
        <Box sx={{ width: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {hasChildren && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              {isExpanded ? (
                <CaretDown size={14} color={colors.textSecondary} />
              ) : (
                <CaretRight size={14} color={colors.textSecondary} />
              )}
            </Box>
          )}
        </Box>
        <Checkbox
          checked={checkState === 'checked'}
          indeterminate={checkState === 'indeterminate'}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{
            padding: '2px',
            color: colors.textSecondary,
            '&.Mui-checked': { color: colors.accent },
            '&.MuiCheckbox-indeterminate': { color: colors.accent },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', width: '20px', justifyContent: 'center', flexShrink: 0 }}>
          {getTypeIcon(field.type, colors.textSecondary)}
        </Box>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: colors.text,
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
          }}
        >
          {field.key}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: isNoData ? colors.noData : colors.textSecondary,
            fontStyle: isNoData ? 'italic' : 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            fontFamily: 'monospace',
          }}
        >
          {isNoData ? 'no data' : field.preview}
        </Typography>
      </Box>
      {hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          {field.children!.map((child) => (
            <FieldRow
              key={child.path}
              field={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              expandedPaths={expandedPaths}
              onTogglePath={onTogglePath}
              onToggleExpand={onToggleExpand}
              isDark={isDark}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

export default function FilterFieldList({ fields, selectedPaths, onTogglePath, onToggleAll, theme }: FilterFieldListProps) {
  const isDark = theme === 'dark';
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const selectedSet = useMemo(() => new Set(selectedPaths), [selectedPaths]);
  const { selected, total } = useMemo(() => getSelectedCount(fields, selectedPaths), [fields, selectedPaths]);
  const handleToggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingY: '4px' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {selected} of {total} selected
        </Typography>
        <Typography
          onClick={onToggleAll}
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: colors.accent,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Select all
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {fields.map((field) => (
          <FieldRow
            key={field.path}
            field={field}
            depth={0}
            selectedSet={selectedSet}
            expandedPaths={expandedPaths}
            onTogglePath={onTogglePath}
            onToggleExpand={handleToggleExpand}
            isDark={isDark}
          />
        ))}
      </Box>
    </Box>
  );
}
